import {
  DEFAULT_CHART_PARAM,
  LINGERIE_CHART_PARAM,
} from "../types/types";

export const SIZING_GENDER = {
  MALE: "male",
  FEMALE: "female",
};

export const SIZING_CLOTHING = {
  TOP: "top",
  BOTTOM: "bottom",
  FULL_BODY: "fullbody",
  SWIM: "swim",
  LINGERIE: "lingerie",
};

function isFullBodyClothingType(clothingType) {
  return (
    clothingType === SIZING_CLOTHING.FULL_BODY ||
    clothingType === SIZING_CLOTHING.SWIM
  );
}

/**
 * Primary measurement keys that must be present (non-null) for each clothing type.
 */
export function getPrimaryMeasurementKeys(clothingType) {
  switch (clothingType) {
    case SIZING_CLOTHING.TOP:
      return ["chest"];
    case SIZING_CLOTHING.BOTTOM:
      return ["waist"];
    case SIZING_CLOTHING.FULL_BODY:
    case SIZING_CLOTHING.SWIM:
      return ["chest", "hip"];
    case SIZING_CLOTHING.LINGERIE:
      return ["chest", "hip"];
    default:
      return ["chest"];
  }
}

/**
 * Resolve API category + template chart id from gender + clothing.
 * Uses existing template ids from default / lingerie bundles.
 */
export function resolveTemplateSelection({
  gender,
  clothingType,
  defaultItems = [],
  lingerieItems = [],
}) {
  if (clothingType === SIZING_CLOTHING.LINGERIE) {
    const id = lingerieItems[0]?.id;
    return id
      ? { categoryParam: LINGERIE_CHART_PARAM, templateId: id }
      : { categoryParam: LINGERIE_CHART_PARAM, templateId: null };
  }

  let templateId = null;
  if (gender === SIZING_GENDER.MALE) {
    if (
      clothingType === SIZING_CLOTHING.TOP ||
      isFullBodyClothingType(clothingType)
    ) {
      templateId = "maleTopSizingChart";
    } else if (clothingType === SIZING_CLOTHING.BOTTOM) {
      templateId = "maleBottomSizingChart";
    }
  } else {
    if (
      clothingType === SIZING_CLOTHING.TOP ||
      isFullBodyClothingType(clothingType)
    ) {
      templateId = "femaleTopSizingChart";
    } else if (clothingType === SIZING_CLOTHING.BOTTOM) {
      templateId = "femaleBottomSizingChart";
    }
  }

  const exists = defaultItems.some((c) => c.id === templateId);
  return {
    categoryParam: DEFAULT_CHART_PARAM,
    templateId: exists ? templateId : defaultItems[0]?.id ?? null,
  };
}

/**
 * For swim, merge hip ranges from the matching bottom template into top template sizes.
 */
/**
 * Best-effort inference from API template id (e.g. maleTopSizingChart).
 * Top vs swim cannot be distinguished by id alone — defaults to TOP.
 */
export function inferGenderAndClothingFromChartId(chartId) {
  if (!chartId || typeof chartId !== "string") return null;
  const lower = chartId.toLowerCase();
  if (lower.includes("lingerie")) {
    return {
      gender: SIZING_GENDER.FEMALE,
      clothingType: SIZING_CLOTHING.LINGERIE,
    };
  }
  const isMale = lower.startsWith("male");
  const isFemale = lower.startsWith("female");
  if (!isMale && !isFemale) return null;
  const gender = isMale ? SIZING_GENDER.MALE : SIZING_GENDER.FEMALE;
  if (lower.includes("bottom")) {
    return { gender, clothingType: SIZING_CLOTHING.BOTTOM };
  }
  if (lower.includes("top")) {
    return { gender, clothingType: SIZING_CLOTHING.TOP };
  }
  return { gender, clothingType: SIZING_CLOTHING.TOP };
}

export function augmentSizesForSwim({
  topChart,
  bottomChart,
  clothingType,
}) {
  if (!isFullBodyClothingType(clothingType) || !topChart?.sizes?.length) {
    return topChart;
  }
  if (!bottomChart?.sizes?.length) return topChart;

  const byName = Object.fromEntries(
    bottomChart.sizes.map((s) => [s.name, s])
  );

  const sizes = topChart.sizes.map((row) => {
    const hipFromBottom = byName[row.name]?.hip;
    return {
      ...row,
      hip:
        hipFromBottom != null
          ? hipFromBottom
          : row.hip != null
            ? row.hip
            : { min: 0, max: 0 },
    };
  });

  return { ...topChart, sizes };
}
